import type { ColumnDef } from '@tanstack/react-table';
import { ImageIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PostManagerResponseDTO } from '@/api/generated/model';
import { toPostRow } from './types';
import { safeMediaSrc, isRasterMediaSrc } from '@/lib/mediaUrl';
import {
  contentTypeLabel,
  normalizePostStatusKey,
  normalizeModerationStatusKey,
  postStatusLabel,
  postStatusBadgeVariant,
  postPublicationHint,
  moderationStatusLabel,
  moderationStatusBadgeVariant,
  displayModerationStatus,
  formatReleaseTime,
  formatUsd,
  formatElapsedSince,
  scanProcessingStartedAt,
  truncateScanError,
} from './lib/contentDisplay';

/** True when the post is in a hidden/terminated state. */
function isHiddenStatus(status: string | null): boolean {
  const code = normalizePostStatusKey(status);
  return code === 'HID' || code === 'TER';
}

function riskColor(risk: number | null): string {
  if (risk == null) return 'text-muted-foreground';
  if (risk >= 0.8) return 'text-red-600 font-semibold';
  if (risk >= 0.5) return 'text-amber-600 font-medium';
  return 'text-green-600';
}

// Columns match CMP order/labels (doc 08 §4.6). Rows are raw DTOs; each cell maps via toPostRow.
export const contentColumns: ColumnDef<PostManagerResponseDTO, unknown>[] = [
  {
    id: 'id',
    header: 'Post ID',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
        {toPostRow(row.original).id || '—'}
      </span>
    ),
  },
  {
    id: 'preview',
    header: 'Preview',
    // Fixed width so long titles truncate instead of expanding the column and
    // pushing Views/Type/Status/etc. off-screen (table auto-layout ignores truncate alone).
    cell: ({ row }) => {
      const r = toPostRow(row.original);
      const thumb = safeMediaSrc(r.thumbnail);
      const thumbIsRaster = isRasterMediaSrc(r.thumbnail);
      const hidden = isHiddenStatus(r.postStatus);
      const title = r.title || '—';
      return (
        <div className="flex w-64 max-w-64 items-center gap-3">
          {thumbIsRaster ? (
            <img
              src={thumb}
              alt=""
              className={cn(
                'size-10 shrink-0 rounded object-cover',
                hidden && 'blur-[2px] grayscale',
              )}
              loading="lazy"
            />
          ) : (
            <div className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
              <ImageIcon className="size-4" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div
              className={cn('truncate text-sm font-medium', hidden && 'text-muted-foreground')}
              title={title}
            >
              {title}
            </div>
            <div className="truncate text-xs text-muted-foreground" title={r.handle}>
              {r.handle}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    id: 'views',
    header: 'Views/Likes',
    cell: ({ row }) => {
      const r = toPostRow(row.original);
      return (
        <span className="whitespace-nowrap text-sm tabular-nums">
          {r.viewCount}/{r.likeCount}
        </span>
      );
    },
  },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => {
      const r = toPostRow(row.original);
      return (
        <span className="whitespace-nowrap text-sm">{contentTypeLabel(r.type, r.fileType)}</span>
      );
    },
  },
  {
    id: 'postStatus',
    header: 'TipMi visibility',
    cell: ({ row }) => {
      const status = toPostRow(row.original).postStatus;
      const publicationHint = postPublicationHint(status);
      return (
        <div className="flex flex-col items-start gap-0.5 whitespace-nowrap">
          <Badge variant={postStatusBadgeVariant(status)}>{postStatusLabel(status)}</Badge>
          {publicationHint ? (
            <span className="text-xs text-muted-foreground">{publicationHint}</span>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'moderationStatus',
    header: 'TipMi ledger',
    cell: ({ row }) => {
      const r = toPostRow(row.original);
      const status = displayModerationStatus(r.moderationStatus, {
        combinedRisk: r.combinedRisk,
        riskTier: r.riskTier,
      });
      const key = normalizeModerationStatusKey(status);
      // Ledger pending clock only — never Bedrock attempt / release time fallbacks.
      const startedAt = scanProcessingStartedAt({
        moderationStartedAt: r.moderationStartedAt,
      });
      const elapsed = key === 'PENDING' ? formatElapsedSince(startedAt) : null;
      // TipMi ledger is publication state only — never Bedrock classify / is_add_category.
      const ledgerError =
        key === 'ERROR' ? (r.moderationLastError ?? '').trim() || null : null;
      return (
        <div className="flex max-w-[220px] flex-col items-start gap-0.5">
          <Badge variant={moderationStatusBadgeVariant(status)}>{moderationStatusLabel(status)}</Badge>
          {elapsed ? (
            <span className="text-xs text-muted-foreground">Pending · {elapsed}</span>
          ) : null}
          {ledgerError ? (
            <span className="line-clamp-2 text-xs text-destructive" title={ledgerError}>
              {truncateScanError(ledgerError)}
            </span>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'risk',
    header: 'Risk Score',
    cell: ({ row }) => {
      const r = toPostRow(row.original);
      const risk = r.combinedRisk;
      return (
        <div className="flex flex-col items-start gap-0.5 whitespace-nowrap">
          <span className={cn('text-sm tabular-nums', riskColor(risk))}>
            {risk != null ? (risk * 100).toFixed(0) + '%' : '—'}
          </span>
          {r.riskTier ? (
            <span className="text-xs text-muted-foreground">{r.riskTier}</span>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'time',
    header: 'Time',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-sm text-muted-foreground">
        {formatReleaseTime(toPostRow(row.original).releaseTime)}
      </span>
    ),
  },
  {
    id: 'earnings',
    header: 'Earnings',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-sm tabular-nums">
        {formatUsd(toPostRow(row.original).earnings)}
      </span>
    ),
  },
];
